// Saved items ("My items"): products and services kept for reuse. A port of
// SavedItem in packages/paidlark_core/lib/data/models/saved_item.dart, written
// field for field as its toJson writes them, in users/{uid}/items.
import { nowStamp } from '@/core/dates';
import { newId } from '@/core/ids';
import type { LineItem } from '@/core/invoice';

export interface SavedItem {
  id: string;
  name: string;
  description: string;
  /** Its price in minor units of [currency]. */
  rateMinor: number;
  currency: string;
  /** The business it belongs to; null for items from before there could be several (the first business's). */
  businessId: string | null;
  /** ISO 8601 UTC. */
  createdAt: string;
  updatedAt: string;
}

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

export function savedItemFromJson(json: Record<string, unknown>): SavedItem {
  if (typeof json.id !== 'string') throw new Error('A saved item needs an id');
  return {
    id: json.id,
    name: str(json.name),
    description: str(json.description),
    rateMinor: typeof json.rateMinor === 'number' ? Math.trunc(json.rateMinor) : 0,
    currency: str(json.currency, 'NGN'),
    businessId: typeof json.businessId === 'string' && json.businessId ? json.businessId : null,
    createdAt: str(json.createdAt),
    updatedAt: str(json.updatedAt),
  };
}

export function savedItemToJson(item: SavedItem): Record<string, unknown> {
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    rateMinor: item.rateMinor,
    currency: item.currency,
    ...(item.businessId ? { businessId: item.businessId } : {}),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

/** Most recently saved or changed first, then by name. */
export function sortSavedItems(items: SavedItem[]): SavedItem[] {
  return [...items].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

/** Whether [name] is this item's, ignoring case and spaces at the ends. */
export const isNamed = (item: SavedItem, name: string) => item.name.trim().toLowerCase() === name.trim().toLowerCase();

/**
 * The item to store for a line of [currency]: the saved one with the same
 * name and currency, updated, or a new one. Never two of the same name.
 */
export function rememberedItem(
  items: SavedItem[],
  line: { name: string; description: string; rateMinor: number },
  currency: string,
  businessId: string | null,
  now: string = nowStamp(),
): { item: SavedItem; isNew: boolean } {
  const same = items.find((s) => isNamed(s, line.name) && s.currency === currency);
  const fields = { name: line.name.trim(), description: line.description.trim(), rateMinor: line.rateMinor, currency, updatedAt: now };
  return same ? { item: { ...same, ...fields }, isNew: false } : { item: { id: newId(), createdAt: now, businessId, ...fields }, isNew: true };
}

/** Whether [items] already hold [line] as it is: same name, currency, rate and description. */
export function isRemembered(items: SavedItem[], line: { name: string; description: string; rateMinor: number }, currency: string): boolean {
  return items.some((s) => isNamed(s, line.name) && s.currency === currency && s.rateMinor === line.rateMinor && s.description.trim() === line.description.trim());
}

/** A new line for an invoice: one of [item], under a new id. */
export function lineFromSaved(item: SavedItem): LineItem {
  return { id: newId(), name: item.name, description: item.description, quantity: 1, rateMinor: item.rateMinor };
}
